// Lib
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import { isEmpty } from 'lodash';

import { getLabels } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import {
    selectBoardsToFetchForCurrentPath,
    selectCurrentPathLabels,
} from '../../../../../../element/labels/labelSelectors';

// Components
import LabelListElement from './LabelListElement';

// Types
import { Label, LabelsByBoards } from '../../../../../../../common/labels/labelTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { fetchBoards } from '../../../../../../element/board/boardService';

type Props = {
    handleToggleLabelSelection: (label: Label) => void;
    handleClickEdit: (label: Label) => void;
    elements: List<ImMNElement>;
    searchString: string;
    allLabels: Label[];
};

/**
 * Merge the existing and latest 'current labels' lists, while ensuring that only available labels are included and that
 * new labels are added. The only difference from using the latest list directly is that labels that are no longer
 * available due to being removed from the last element will still appear in the list.
 */
const mergeCurrentLabels = (
    existingCurrentLabels: LabelsByBoards,
    latestCurrentLabels: LabelsByBoards,
    availableLabelIds: Set<string>,
): LabelsByBoards => {
    const mergedLabelsByBoards = existingCurrentLabels
        .map(({ title, labels }) => ({
            title,
            labels: labels.filter((label) => availableLabelIds.has(label.id)),
        }))
        .filter(({ labels }) => labels.length > 0);

    const existingLabelIds = new Set(mergedLabelsByBoards.flatMap((group) => group.labels.map((label) => label.id)));

    latestCurrentLabels.forEach(({ title, labels }) => {
        const labelsToAdd = labels.filter(
            (label) => availableLabelIds.has(label.id) && !existingLabelIds.has(label.id),
        );
        if (!labelsToAdd.length) return;

        labelsToAdd.forEach((label) => existingLabelIds.add(label.id));

        const existingGroup = mergedLabelsByBoards.find((group) => group.title === title);
        if (existingGroup) {
            existingGroup.labels = [...existingGroup.labels, ...labelsToAdd];
            return;
        }

        mergedLabelsByBoards.push({
            title,
            labels: labelsToAdd,
        });
    });

    return mergedLabelsByBoards;
};

const LabelLists = (props: Props) => {
    const { elements, searchString, allLabels, handleToggleLabelSelection, handleClickEdit } = props;

    // Some elements on ancestor boards may not have been fetched yet
    const { toFetch, isBoardFetchInProgress } = useSelector(selectBoardsToFetchForCurrentPath);
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(fetchBoards({ boardIds: toFetch }));
    }, [toFetch]);

    // Immediately display updates like adding new labels or deleting existing ones,
    // but don't remove labels that are no longer used while the popup is open
    const latestCurrentLabels = useSelector(selectCurrentPathLabels);
    const [currentLabels, setCurrentLabels] = React.useState(latestCurrentLabels);
    useEffect(() => {
        // While fetching, always update to the latest
        if (isBoardFetchInProgress) {
            setCurrentLabels(latestCurrentLabels);
            return;
        }

        const availableLabelIds = new Set(allLabels.map((label) => label.id));
        setCurrentLabels((previousCurrentLabels) =>
            mergeCurrentLabels(previousCurrentLabels, latestCurrentLabels, availableLabelIds),
        );
    }, [allLabels, latestCurrentLabels, isBoardFetchInProgress]);

    const allLabelsFiltered = useMemo(
        () =>
            allLabels.filter(
                (label) => isEmpty(searchString) || label.name.toLowerCase().includes(searchString.toLowerCase()),
            ),
        [allLabels, searchString],
    );

    const appliedLabelIds = elements.flatMap((element) => getLabels(element) || []) as Immutable.List<string>;

    return (
        <div className="LabelLists">
            {searchString ? (
                <div className="mb-3">
                    {allLabelsFiltered.map((label) => (
                        <LabelListElement
                            key={label.id}
                            label={label}
                            isSelected={appliedLabelIds?.includes(label.id)}
                            handleClickLabel={handleToggleLabelSelection}
                            handleClickEdit={handleClickEdit}
                            size="sm"
                        />
                    ))}
                </div>
            ) : (
                <div>
                    {currentLabels.map(({ title, labels }, index) => {
                        return (
                            <div key={index} className="mb-3">
                                {title && <p className="w-full text-left mb-1">{title}</p>}
                                {labels.map((label) => {
                                    return (
                                        <LabelListElement
                                            key={label.id}
                                            label={label}
                                            isSelected={appliedLabelIds?.includes(label.id)}
                                            handleClickLabel={handleToggleLabelSelection}
                                            handleClickEdit={handleClickEdit}
                                            size="sm"
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LabelLists;
